/**
 * Service Worker Registration Utility
 *
 * This utility provides functions for registering the service worker,
 * checking for updates, and managing communication between the service worker
 * and the main application.
 */

// Type declarations for Background Sync API (not included in standard lib.dom)
declare global {
  interface ServiceWorkerRegistration {
    sync: SyncManager;
  }

  interface SyncManager {
    register(tag: string): Promise<void>;
    getTags(): Promise<string[]>;
  }
}

// Constants
const SW_VERSION = '1.0.0';
const SW_PATH = '/museum-app/service-worker.js';

// Interface for registration options
interface RegisterOptions {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
  onOffline?: () => void;
  onOnline?: () => void;
  scope?: string;
}

/**
 * Register the service worker
 */
export const register = (options: RegisterOptions = {}): void => {
  // Only register in production and if service worker is supported
  if ('serviceWorker' in navigator) {
    const { onSuccess, onUpdate, onError, scope } = options;

    window.addEventListener('load', async () => {
      try {
        // Register the service worker
        const registration = await navigator.serviceWorker.register(SW_PATH, {
          scope: scope || '/'
        });

        console.log('ServiceWorker registration successful with scope:', registration.scope);

        // Set up update detection
        setupUpdates(registration, onUpdate);

        // Notify success
        if (onSuccess) {
          onSuccess(registration);
        }

        // Set up message handling
        setupMessageHandling();

        // Setup online/offline detection
        setupConnectivityDetection(options);
      } catch (error) {
        console.error('ServiceWorker registration failed:', error);

        if (onError) {
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      }
    });
  }
};

/**
 * Setup functionality to detect service worker updates
 */
const setupUpdates = (
  registration: ServiceWorkerRegistration,
  onUpdate?: (registration: ServiceWorkerRegistration) => void
): void => {
  // Check for updates when a new service worker is waiting
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;

    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
      // When the new service worker is installed, notify the user of an update
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        console.log('New service worker available; update available');

        if (onUpdate) {
          onUpdate(registration);
        }

        // Notify the user that an update is available
        showUpdateNotification();
      }
    });
  });
};

/**
 * Show a notification to the user that an update is available
 */
const showUpdateNotification = (): void => {
  // This can be customized to show a UI notification instead of a console message
  console.log('A new version of the app is available. Reload to update.');

  // You can dispatch a custom event to notify components that need to display an update UI
  window.dispatchEvent(new CustomEvent('swUpdateAvailable'));
};

/**
 * Setup message handling between the page and service worker
 */
const setupMessageHandling = (): void => {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data;

    if (!data) return;

    // Handle different message types from the service worker
    switch (data.type) {
      case 'SYNC_VISITOR_NOTES':
        console.log('Syncing visitor notes from service worker');
        window.dispatchEvent(new CustomEvent('syncVisitorNotes'));
        break;

      case 'SYNC_ANALYTICS':
        console.log('Syncing analytics data from service worker');
        window.dispatchEvent(new CustomEvent('syncAnalytics'));
        break;

      case 'CACHE_COMPLETE':
        console.log(`Caching complete for: ${data.resources}`);
        window.dispatchEvent(
          new CustomEvent('cachingComplete', { detail: data.resources })
        );
        break;

      case 'CACHE_ERROR':
        console.error(`Caching error: ${data.error}`);
        window.dispatchEvent(
          new CustomEvent('cachingError', { detail: data.error })
        );
        break;
    }
  });
};

/**
 * Setup online/offline detection
 */
const setupConnectivityDetection = (options: RegisterOptions): void => {
  const { onOffline, onOnline } = options;

  // Handle offline event
  window.addEventListener('offline', () => {
    console.log('App is offline');

    if (onOffline) {
      onOffline();
    }

    // Dispatch offline event for components to respond to
    window.dispatchEvent(new CustomEvent('appOffline'));
  });

  // Handle online event
  window.addEventListener('online', () => {
    console.log('App is online');

    if (onOnline) {
      onOnline();
    }

    // Dispatch online event for components to respond to
    window.dispatchEvent(new CustomEvent('appOnline'));

    // Trigger sync when back online
    triggerSyncWhenOnline();
  });
};

/**
 * Trigger background sync when the app comes back online
 */
const triggerSyncWhenOnline = async (): Promise<void> => {
  if (!navigator.serviceWorker.controller) return;

  try {
    // Request a background sync for visitor notes
    await registerBackgroundSync('sync-visitor-notes');

    // Request a background sync for analytics
    await registerBackgroundSync('sync-analytics');
  } catch (error) {
    console.error('Background sync registration failed:', error);
  }
};

/**
 * Register a background sync with the service worker
 */
const registerBackgroundSync = async (syncTag: string): Promise<void> => {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(syncTag);
      console.log(`Background sync registered: ${syncTag}`);
    } catch (error) {
      console.error(`Failed to register background sync for ${syncTag}:`, error);
    }
  } else {
    console.log('Background Sync not supported');
  }
};

/**
 * Send a message to the service worker
 */
export const sendMessageToSW = (message: any): void => {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
};

/**
 * Request caching of language assets
 */
export const cacheLanguages = (languages: string[]): void => {
  sendMessageToSW({
    type: 'CACHE_LANGUAGES',
    languages,
  });
};

/**
 * Request caching of exhibit assets
 */
export const cacheExhibit = (exhibitId: string): void => {
  sendMessageToSW({
    type: 'CACHE_EXHIBIT',
    exhibitId,
  });
};

/**
 * Request caching of room assets
 */
export const cacheRoom = (roomId: string): void => {
  sendMessageToSW({
    type: 'CACHE_ROOM',
    roomId,
  });
};

/**
 * Unregister all service workers and clear caches
 */
export const unregisterServiceWorkers = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    try {
      // Get all service worker registrations
      const registrations = await navigator.serviceWorker.getRegistrations();

      // Unregister each service worker
      for (const registration of registrations) {
        await registration.unregister();
        console.log('ServiceWorker unregistered');
      }

      // Clear all caches
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      console.log('All caches cleared');

      return Promise.resolve();
    } catch (error) {
      console.error('Error unregistering service workers:', error);
      return Promise.reject(error);
    }
  }
};
